import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { apiRequest } from "@/lib/queryClient";

/** Save only this field: a list row must not overwrite stale store details. */
export function PriorityStoreCheckbox({ storeId, storeName, checked }: {
  storeId: number;
  storeName: string;
  checked: boolean;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: async (isPriorityStore: boolean) => {
      // Admin configuration must be confirmed online, not queued as a fake success.
      if (!navigator.onLine) throw new Error("Connect to the internet to change priority.");
      await apiRequest(`/api/stores/${storeId}`, "PATCH", { isPriorityStore });
    },
    retry: false,
    onMutate: () => setError(null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/stores"] });
    },
    onError: () => setError("Priority was not saved. Please check your connection and try again."),
  });
  const id = `priority-store-${storeId}`;
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex min-h-11 items-center gap-3 rounded-md border px-3">
        <Checkbox
          id={id}
          checked={checked}
          disabled={mutation.isPending}
          aria-label={`Priority store: ${storeName}`}
          aria-describedby={error ? `${id}-error` : undefined}
          onCheckedChange={(value) => mutation.mutate(value === true)}
        />
        <Label htmlFor={id} className="cursor-pointer py-3 text-sm font-semibold">
          {mutation.isPending ? "Saving priority…" : "Priority store"}
        </Label>
      </div>
      {error && <p id={`${id}-error`} role="alert" className="max-w-xs text-sm text-destructive">{error}</p>}
    </div>
  );
}
